import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Brain, TrendingUp, Users, DollarSign } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const McKinseyAIArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-purple-500 text-white">
                <Brain className="h-3 w-3 mr-1" />
                {t('article.mckinsey.badge')}
              </Badge>
              <span className="text-gray-500 text-sm">{t('article.mckinsey.date')}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.mckinsey.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.mckinsey.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
              alt="AI automation and robotics transforming workplace" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Brain className="h-5 w-5 mr-2 text-purple-600" />
                  {t('article.mckinsey.stat1')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">{t('article.mckinsey.statValue1')}</div>
                <p className="text-sm text-gray-600">{t('article.mckinsey.statDesc1')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                  {t('article.mckinsey.stat2')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{t('article.mckinsey.statValue2')}</div>
                <p className="text-sm text-gray-600">{t('article.mckinsey.statDesc2')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Users className="h-5 w-5 mr-2 text-blue-600" />
                  {t('article.mckinsey.stat3')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{t('article.mckinsey.statValue3')}</div>
                <p className="text-sm text-gray-600">{t('article.mckinsey.statDesc3')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('article.mckinsey.heading1')}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.mckinsey.content1')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.mckinsey.heading2')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.mckinsey.content2')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.mckinsey.skillChangeIndex')}</h3>
            <div className="bg-purple-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.mckinsey.skillsTitle')}</h4>
              <ul className="space-y-2">
                <li>{t('article.mckinsey.skillsHighly')}</li>
                <li>{t('article.mckinsey.skillsLeast')}</li>
                <li>{t('article.mckinsey.skillsEvolving')}</li>
                <li>{t('article.mckinsey.skillsGrowing')}</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.mckinsey.heading3')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('article.mckinsey.marketSignals')}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>{t('article.mckinsey.marketFluency')}</li>
              <li>{t('article.mckinsey.marketTechnical')}</li>
              <li>{t('article.mckinsey.marketComplementary')}</li>
              <li>{t('article.mckinsey.marketDeclining')}</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.mckinsey.physicalVsNonphysical')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.mckinsey.physicalWork')} {t('article.mckinsey.physicalWorkContinued')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.mckinsey.implementationChallenges')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.mckinsey.implementationFactors')}</h4>
              <ul className="space-y-2">
                <li>{t('article.mckinsey.implementationTechnical')}</li>
                <li>{t('article.mckinsey.implementationEconomic')}</li>
                <li>{t('article.mckinsey.implementationOrganizational')}</li>
                <li>{t('article.mckinsey.implementationRegulatory')}</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.mckinsey.heading4')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.mckinsey.content4')} {t('article.mckinsey.leadershipContinued')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.mckinsey.heading5')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.mckinsey.futureWorkforce')}
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">{t('article.mckinsey.sourceTitle')}</h4>
              <p className="text-sm text-gray-600">
                {t('article.mckinsey.sourceText')}
                <a href="https://www.mckinsey.com/mgi/our-research/agents-robots-and-us-skill-partnerships-in-the-age-of-ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  {t('article.mckinsey.viewSource')}
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default McKinseyAIArticle;